import { v4 as generateUuid } from 'uuid';
import { utcToZonedTime } from 'date-fns-tz';
import { addMonths, format } from 'date-fns';
import { PPC_PAGE_TITLE } from '@css-xp/ppc-constants';
import { CCPPricing } from '@atlassian/cssxp-typescommon';
import { transformBillingAdmins } from '@css-xp/ppc-purchase-experience';
import {
  CartLinkedEntitlementsSchema,
  CartProductSchema,
  CloudSiteTypeEnum,
  OperationType,
  ProductStatus,
  BillingProfileResponse,
  LinkedEntitlementRelationShipType,
} from './types';
import { NewProductLineItem } from './ui/add-new-products/src/types';
import { AppPromotionResponse, CurrentScreenTypeEnum } from './store/types';

export const transformLineItemsToCartProduct = (productLineItem: NewProductLineItem): CartProductSchema => ({
  id: productLineItem.lineItem?.lineItemId,
  offeringId: productLineItem.lineItem?.offeringKey,
  name: productLineItem.lineItem?.name,
  cloudSiteId: productLineItem.cloudSiteId,
  cloudSite: productLineItem.cloudSite,
  containerId: productLineItem.containerId,
  cloudSiteType: productLineItem.cloudSiteId ? CloudSiteTypeEnum.OWN : CloudSiteTypeEnum.NEW,
  activeUsers: String(productLineItem.lineItem?.chargeQuantities[0].quantity),
  billingCycle: productLineItem.lineItem?.billingCycleLabel,
  customerContacts: [],
  entitlementNumber: '',
  listPrice: undefined,
  partnerMargin: undefined,
  otherDiscount: undefined,
  nextBillDate: '',
  nextBillAnchorDate: '',
  pricingPlanId: productLineItem.lineItem.pricingPlanId ?? '',
  productId: productLineItem.lineItem?.productKey,
  productStatuses: [ProductStatus.NEW],
  operationType: OperationType.NEW,
  trialLengthInDays: productLineItem?.trialLengthInDays,
  inTrial: productLineItem.lineItem?.isFreeProduct
    ? !productLineItem.lineItem?.isFreeProduct
    : !!Number(productLineItem?.trialLengthInDays),
  promotions: [],
  isExternalProduct: productLineItem.isExternalProduct,
  hasAllData: true, // this is hardcoded as when line items from catalog are transformed into cart product schema, they have all the data they need
  inactiveReason: productLineItem.inactiveReason,
});

export const transformEntitlementToCartLinkedEntitlement = ({
  entitlement,
  containerOrProductId,
  operationType,
  hasAllData = true,
  isAddOn = false,
}: {
  entitlement: any;
  containerOrProductId: string;
  operationType: OperationType;
  hasAllData?: boolean;
  isAddOn?: boolean;
}): CartLinkedEntitlementsSchema => ({
  id: entitlement.id,
  itemId: generateUuid(),
  name: entitlement.productName ?? '',
  cloudSiteId: entitlement.cloudSiteId ?? '',
  cloudSite: entitlement.cloudSiteHostname ?? 'Other',
  cloudSiteType: entitlement.cloudSiteId ? CloudSiteTypeEnum.OWN : CloudSiteTypeEnum.NEW,
  logoId: entitlement.logoId ?? '',
  containerOrProductId,
  activeUsers: entitlement.activeUsers ? String(entitlement.activeUsers) : '',
  billingCycle: entitlement.billingFrequency ?? '',
  customerContacts: entitlement.endCustomerContacts ?? [],
  entitlementNumber: entitlement.entitlementNumber ?? '',
  productId: entitlement.productId,
  pricingPlanId: entitlement.pricingPlanId ?? '',
  pricingPlanType: entitlement.pricingPlan?.pricingPlanType,
  priceEligibility: entitlement.pricingPlan?.type,
  offeringName: entitlement?.offering?.name ?? 'Standard',
  offeringId: entitlement.offeringId,
  offeringLevel: entitlement?.offering?.level,
  operationType,
  listPrice: undefined,
  partnerMargin: undefined,
  otherDiscount: undefined,
  nextBillDate: entitlement.nextBillingTimestamp ?? '',
  nextBillAnchorDate: entitlement.nextBillingAnchorTimestamp ?? '',
  promotions: entitlement.promotions,
  productStatuses: operationType === OperationType.TRANSFER ? [ProductStatus.TRANSFER] : [],
  sourceInvoiceGroupId: entitlement.billingGroupId ?? '',
  inTrial: entitlement.trial === true,
  isPartnerEntitlement: entitlement.isPartnerEntitlement,
  hasAllData,
  billingAdmins: transformBillingAdmins(entitlement?.billingAdmins),
  isSandbox: entitlement?.isSandbox,
  sandboxParent: entitlement?.sandboxParent,
  isFutureDatedSubscription: entitlement.isFutureDatedSubscription,
  inactiveReason: entitlement.inactiveReason,
  relationshipType: isAddOn ? LinkedEntitlementRelationShipType.ADD_ON : LinkedEntitlementRelationShipType.APP,
  isOnUpgradeTrial: !!entitlement.isOnUpgradeTrial,
});

export const transformLineItemsToCartApp = (
  appLineItem: any,
  trialLengthInDays: string,
  promotions: AppPromotionResponse[]
): CartLinkedEntitlementsSchema => ({
  id: appLineItem?.lineItemId,
  name: appLineItem?.name,
  logoId: appLineItem?.logoId ?? '',
  billingCycle: appLineItem.billingCycleLabel ?? '',
  containerOrProductId: '',
  activeUsers: String(appLineItem?.chargeQuantities[0].quantity),
  customerContacts: appLineItem.endCustomerContacts ?? [],
  productId: appLineItem?.productKey,
  pricingPlanId: appLineItem?.pricingPlanKey ?? appLineItem?.pricingPlanId ?? '',
  offeringId: appLineItem?.offeringId ?? appLineItem?.offeringKey ?? '',
  offeringName: appLineItem?.offeringName ?? 'Standard',
  operationType: OperationType.NEW,
  productStatuses: [ProductStatus.NEW],
  trialLengthInDays,
  promotions,
  parentEntitlementId: appLineItem?.parentEntitlementId ?? '',
  nextBillingTimestamp: appLineItem?.nextBillingTimestamp ?? '',
  inTrial: !!Number(trialLengthInDays),
  hasAllData: true, // this is hardcoded as when line items from catalog are transformed into cart app schema, they have all the data they need
});

export const truthy = <T>(value: T): value is NonNullable<T> => !!value;

export const getCurrentScreenPageTitle = (currentScreen?: string): string => {
  if (currentScreen === CurrentScreenTypeEnum.ORDER_SUMMARY) {
    return PPC_PAGE_TITLE.REVIEW_ORDER;
  } else if (currentScreen === CurrentScreenTypeEnum.ORDER_CONFIRMATION) {
    return PPC_PAGE_TITLE.ORDER_RECEIVED;
  }

  return PPC_PAGE_TITLE.ORDER;
};

export const getAdjustedDateForMonthEstimate = (nextBillDate?: number) => {
  /**
   * If nextBillDate is present, then using this nextBillDate to do calculation.
   * Else, using current date to do calculation.
   */
  const date = nextBillDate ? new Date(nextBillDate) : new Date();
  const currentDateInUTC = utcToZonedTime(date, 'UTC');
  const currentDay = format(currentDateInUTC, 'd');

  const nextMonth = addMonths(currentDateInUTC, 1);
  nextMonth.setUTCDate(1);
  nextMonth.setUTCHours(0, 0, 0, 0);
  const firstDayOfNextMonthTimeStamp = nextMonth.getTime();

  const secondNextMonth = addMonths(currentDateInUTC, 2);
  secondNextMonth.setUTCDate(1);
  secondNextMonth.setUTCHours(0, 0, 0, 0);
  const firstDayOfSecondNextMonthTimeStamp = secondNextMonth.getTime();

  /**
   * While fetching the estimate on 29th, 30th or 31st of any month, the estimates are generated from today till 28th
   * of the next month. This is happening because that’s the default behavior of
   * CCP - billing anchor date can’t be set for a date greater than 28th.
   *
   * In order to fix this we need to set the subscription start time to the first day of next month
   * and the next billing anchor timestamp to the first day of the second next month, only when current date is 29th, 30th or 31st.
   * As per API contract - https://hello.atlassian.net/wiki/spaces/tintin/pages/3824760759/API+Contract+OMS+Bill-Estimate+V2+subscriptionStartTime
   *
   */
  return {
    isLastThreeDaysOfMonth: currentDay === '31' || currentDay === '30' || currentDay === '29',
    firstDayOfNextMonthTimeStamp,
    firstDayOfSecondNextMonthTimeStamp,
  };
};

export const transformBillingProfileDetails = (billingProfileInfo: BillingProfileResponse) => {
  return {
    id: billingProfileInfo.id,
    name: billingProfileInfo.name,
    currency: billingProfileInfo.currency,
    transactionAccountId: billingProfileInfo.transactionAccount.id,
    isActive: billingProfileInfo.active,
    shipToParty: {
      id: billingProfileInfo.shipToParty.id,
      name: billingProfileInfo.shipToParty.name,
      addressLine1: billingProfileInfo.shipToParty.postalAddress.line1,
      addressLine2: billingProfileInfo.shipToParty.postalAddress.line2,
      city: billingProfileInfo.shipToParty.postalAddress.city,
      country: billingProfileInfo.shipToParty.postalAddress.country,
      state: billingProfileInfo.shipToParty.postalAddress.state,
      phone: billingProfileInfo.shipToParty.postalAddress.phone,
      postcode: billingProfileInfo.shipToParty.postalAddress.postcode,
      taxId: billingProfileInfo.shipToParty.taxId,
      taxIds: billingProfileInfo.shipToParty.taxIds,
    },
    defaultPaymentMethod: {
      id: billingProfileInfo.defaultPaymentMethod,
    },
    shipToPriceEligibility: CCPPricing.COMMERCIAL,
  };
};
